import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import format from 'date-fns/format';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import isSameDay from 'date-fns/isSameDay';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

// Periodo dos relatorios de atendimento.
//
// O dia escolhido no calendario e um dia da CONTA, nao do computador de quem
// olha. Escolher 19/09 numa maquina em UTC+9 e noutra em UTC-5 tem que gerar o
// mesmo par since/until, senao duas pessoas pedem intervalos diferentes e veem
// numeros diferentes do mesmo dia.
//
// O fuso entra pelo NOME IANA: o fim do dia e 23:59:59.999 de relogio de parede
// naquele fuso, resolvido pela base de fusos, nunca por soma de 24 horas. E isso
// que mantem o recorte certo na virada do horario de verao.
//
// Este helper e so para relatorio. getUnixStartOfDay e getUnixEndOfDay do
// DateHelper continuam sendo o dia do navegador para quem os usa fora daqui.

export const DEFAULT_REPORT_TIMEZONE = 'America/Sao_Paulo';

const safeZone = timeZone => timeZone || DEFAULT_REPORT_TIMEZONE;

// Um "dia de calendario" e representado por um Date na meia-noite LOCAL.
// So os campos de calendario (ano, mes, dia) sao lidos; a hora e descartada.
const calendarDay = date => format(date, 'yyyy-MM-dd');

/** Inicio do dia de calendario `date` no fuso `timeZone`, em unix (segundos). */
export const startOfDayInZone = (date, timeZone) =>
  getUnixTime(
    zonedTimeToUtc(`${calendarDay(date)}T00:00:00.000`, safeZone(timeZone))
  );

/** Fim do dia de calendario `date` no fuso `timeZone`, em unix (segundos). */
export const endOfDayInZone = (date, timeZone) =>
  getUnixTime(
    zonedTimeToUtc(`${calendarDay(date)}T23:59:59.999`, safeZone(timeZone))
  );

/**
 * Hoje no fuso da conta, como dia de calendario. Pode ser um dia diferente do
 * "hoje" do navegador: as 23h em Sao Paulo ja e amanha em Londres.
 */
export const todayInZone = (timeZone, now = new Date()) =>
  startOfDay(utcToZonedTime(now, safeZone(timeZone)));

/**
 * Os periodos prontos do seletor, resolvidos em cima de hoje NO FUSO DA CONTA.
 * Devolve dias de calendario, nao instantes.
 */
export const resolveRangeInZone = (rangeType, timeZone, now = new Date()) => {
  const today = todayInZone(timeZone, now);

  const ranges = {
    [DATE_RANGE_TYPES.TODAY]: () => today,
    [DATE_RANGE_TYPES.LAST_7_DAYS]: () => subDays(today, 6),
    [DATE_RANGE_TYPES.LAST_30_DAYS]: () => subDays(today, 29),
    [DATE_RANGE_TYPES.LAST_3_MONTHS]: () => subMonths(today, 3),
    [DATE_RANGE_TYPES.LAST_6_MONTHS]: () => subMonths(today, 6),
    [DATE_RANGE_TYPES.LAST_YEAR]: () => subMonths(today, 12),
    [DATE_RANGE_TYPES.THIS_WEEK]: () => startOfWeek(today, { weekStartsOn: 1 }),
    [DATE_RANGE_TYPES.MONTH_TO_DATE]: () => startOfMonth(today),
  };

  const resolveStart = ranges[rangeType];
  if (!resolveStart) return null;

  return { start: resolveStart(), end: today };
};

/**
 * Par since/until em unix para o backend.
 *
 * Periodo pronto ancorado em hoje (o caso comum) e recalculado no fuso da
 * conta, para o "hoje" ser o da conta mesmo quando o navegador ja virou o dia.
 * Intervalo escolhido a mao, e periodo navegado para outra semana ou outro mes,
 * usam os dias que o calendario entregou: ali quem escolheu o dia foi a pessoa.
 */
export const reportPeriodUnix = ({
  dates,
  rangeType,
  timeZone,
  now = new Date(),
}) => {
  const [pickedStart, pickedEnd] = dates || [];
  const anchoredOnToday =
    rangeType &&
    rangeType !== DATE_RANGE_TYPES.CUSTOM_RANGE &&
    pickedEnd &&
    isSameDay(pickedEnd, now);

  const resolved = anchoredOnToday
    ? resolveRangeInZone(rangeType, timeZone, now)
    : null;

  const start = resolved ? resolved.start : pickedStart;
  const end = resolved ? resolved.end : pickedEnd;

  return {
    from: startOfDayInZone(start, timeZone),
    to: endOfDayInZone(end, timeZone),
  };
};
